/**
 * Inspection Template Editor Hook
 * 
 * Manages template CRUD operations with version control:
 * - RPC mutations for version lifecycle (create_draft, publish, archive)
 * - Direct mutations for structural edits (systems/items) - RLS enforces draft-only
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Json } from '@/integrations/supabase/types';

// ============================================
// Types
// ============================================

export interface InspectionTemplate {
  id: string;
  shop_id: string | null;
  is_global: boolean;
  template_key: string;
  version: number;
  name: string;
  description: string | null;
  is_active: boolean;
  status: 'draft' | 'published' | 'archived';
  settings: Json;
  created_at: string;
  updated_at: string;
  created_by_user_id: string | null;
  published_at: string | null;
  published_by_user_id: string | null;
  change_notes: string | null;
}

export interface TemplateSystem {
  id: string;
  template_id: string;
  system_key: string;
  display_name: string;
  description: string | null;
  sort_order: number;
  is_required: boolean;
  settings: Json;
}

export interface TemplateItem {
  id: string;
  template_id: string;
  system_id: string;
  item_key: string;
  name: string;
  description: string | null;
  shared_key: string | null;
  sort_order: number;
  is_required: boolean;
  evidence_mode: string;
  allowed_evidence: Json;
  measurement_schema: Json;
  ai_policy: Json;
  default_job_catalog_ids: string[] | null;
  settings: Json;
}

export interface TemplateVersion {
  id: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  is_active: boolean;
  created_at: string;
  published_at: string | null;
  change_notes: string | null;
  created_by_user_id: string | null;
  published_by_user_id: string | null;
  system_count?: number;
  item_count?: number;
}

interface NewSystem {
  system_key: string;
  display_name: string;
  description?: string;
  is_required?: boolean;
}

interface SystemUpdate {
  display_name?: string;
  description?: string;
  is_required?: boolean;
}

interface NewItem {
  item_key: string;
  name: string;
  description?: string;
  is_required?: boolean;
  evidence_mode?: string;
  allowed_evidence?: Json;
  ai_policy?: string;
  measurement_schema?: Json;
  default_job_catalog_ids?: string[];
}

interface ItemUpdate {
  name?: string;
  description?: string | null;
  is_required?: boolean;
  evidence_mode?: string;
  allowed_evidence?: Json;
  ai_policy?: string;
  measurement_schema?: Json;
  default_job_catalog_ids?: string[];
}

// ============================================
// Hook
// ============================================

export function useInspectionTemplateEditor(shopId: string, templateId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ============================================
  // Queries
  // ============================================

  // Fetch all templates for the shop (including global)
  const templatesQuery = useQuery({
    queryKey: ['inspection-templates', shopId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inspection_templates')
        .select('*')
        .or(`shop_id.eq.${shopId},is_global.eq.true`)
        .order('template_key')
        .order('version', { ascending: false });

      if (error) throw error;
      return data as InspectionTemplate[];
    },
    enabled: !!shopId,
  });

  // Fetch current template details
  const templateQuery = useQuery({
    queryKey: ['inspection-template', templateId],
    queryFn: async () => {
      if (!templateId) return null;
      
      const { data, error } = await supabase
        .from('inspection_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (error) throw error;
      return data as InspectionTemplate;
    },
    enabled: !!templateId,
  });

  // Fetch systems for current template
  const systemsQuery = useQuery({
    queryKey: ['template-systems', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      
      const { data, error } = await supabase
        .from('inspection_template_systems')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');

      if (error) throw error;
      return data as TemplateSystem[];
    },
    enabled: !!templateId,
  });

  // Fetch items for current template
  const itemsQuery = useQuery({
    queryKey: ['template-items', templateId],
    queryFn: async () => {
      if (!templateId) return [];
      
      const { data, error } = await supabase
        .from('inspection_template_items')
        .select('*')
        .eq('template_id', templateId)
        .order('sort_order');

      if (error) throw error;
      return data as TemplateItem[];
    },
    enabled: !!templateId,
  });

  // Fetch version history for current template's key
  const versionsQuery = useQuery({
    queryKey: ['template-versions', shopId, templateQuery.data?.template_key],
    queryFn: async () => {
      if (!templateQuery.data?.template_key) return [];
      
      const { data, error } = await supabase
        .from('inspection_templates')
        .select('id, version, status, is_active, created_at, published_at, change_notes, created_by_user_id, published_by_user_id')
        .eq('shop_id', shopId)
        .eq('template_key', templateQuery.data.template_key)
        .order('version', { ascending: false });

      if (error) throw error;
      return data as TemplateVersion[];
    },
    enabled: !!templateQuery.data?.template_key,
  });

  // ============================================
  // RPC Mutations (Version Lifecycle)
  // ============================================

  const createDraftMutation = useMutation({
    mutationFn: async ({ templateKey, name, description }: { templateKey: string; name: string; description?: string }) => {
      const { data, error } = await supabase.rpc('manage_inspection_template', {
        p_operation: 'create_draft',
        p_shop_id: shopId,
        p_template_key: templateKey,
        p_name: name,
        p_description: description ?? null,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; template_id?: string; version?: number };
      if (!result.success) {
        throw new Error(result.error || 'Failed to create draft');
      }
      
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates', shopId] });
      toast({
        title: 'Draft created',
        description: `Version ${result.version} draft is ready for editing`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create draft',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const publishDraftMutation = useMutation({
    mutationFn: async ({ templateId, changeNotes }: { templateId: string; changeNotes?: string }) => {
      const { data, error } = await supabase.rpc('manage_inspection_template', {
        p_operation: 'publish',
        p_shop_id: shopId,
        p_template_id: templateId,
        p_change_notes: changeNotes ?? null,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string; template_id?: string; version?: number };
      if (!result.success) {
        throw new Error(result.error || 'Failed to publish');
      }
      
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates', shopId] });
      queryClient.invalidateQueries({ queryKey: ['inspection-template'] });
      queryClient.invalidateQueries({ queryKey: ['template-versions'] });
      toast({
        title: 'Template published',
        description: `Version ${result.version} is now active`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to publish',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: async (targetTemplateId: string) => {
      const { data, error } = await supabase.rpc('manage_inspection_template', {
        p_operation: 'archive',
        p_shop_id: shopId,
        p_template_id: targetTemplateId,
      });

      if (error) throw error;
      
      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to archive');
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inspection-templates', shopId] });
      queryClient.invalidateQueries({ queryKey: ['template-versions'] });
      toast({
        title: 'Template archived',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to archive',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ============================================
  // Direct Mutations (Structural Edits - Draft Only)
  // ============================================

  const verifyDraftStatus = async (targetTemplateId: string) => {
    const { data } = await supabase
      .from('inspection_templates')
      .select('status')
      .eq('id', targetTemplateId)
      .single();
    
    if (data?.status !== 'draft') {
      throw new Error('Cannot modify published or archived templates');
    }
  };

  // Systems
  const addSystemMutation = useMutation({
    mutationFn: async ({ templateId: tid, data: systemData }: { templateId: string; data: NewSystem }) => {
      await verifyDraftStatus(tid);
      
      // Get max sort_order
      const { data: existing } = await supabase
        .from('inspection_template_systems')
        .select('sort_order')
        .eq('template_id', tid)
        .order('sort_order', { ascending: false })
        .limit(1);
      
      const nextOrder = (existing?.[0]?.sort_order ?? -1) + 1;
      
      const { data, error } = await supabase
        .from('inspection_template_systems')
        .insert({
          template_id: tid,
          system_key: systemData.system_key,
          display_name: systemData.display_name,
          description: systemData.description ?? null,
          is_required: systemData.is_required ?? false,
          sort_order: nextOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-systems', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to add system',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateSystemMutation = useMutation({
    mutationFn: async ({ systemId, updates }: { systemId: string; updates: SystemUpdate }) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      const { error } = await supabase
        .from('inspection_template_systems')
        .update(updates)
        .eq('id', systemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-systems', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update system',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteSystemMutation = useMutation({
    mutationFn: async (systemId: string) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      const { error } = await supabase
        .from('inspection_template_systems')
        .delete()
        .eq('id', systemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-systems', templateId] });
      queryClient.invalidateQueries({ queryKey: ['template-items', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete system',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const reorderSystemsMutation = useMutation({
    mutationFn: async ({ templateId: tid, orderedIds }: { templateId: string; orderedIds: string[] }) => {
      await verifyDraftStatus(tid);
      
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('inspection_template_systems')
          .update({ sort_order: index })
          .eq('id', id)
      );
      
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-systems', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to reorder systems',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Items
  const addItemMutation = useMutation({
    mutationFn: async ({ systemId, data: itemData }: { systemId: string; data: NewItem }) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      // Get max sort_order for this system
      const { data: existing } = await supabase
        .from('inspection_template_items')
        .select('sort_order')
        .eq('system_id', systemId)
        .order('sort_order', { ascending: false })
        .limit(1);
      
      const nextOrder = (existing?.[0]?.sort_order ?? -1) + 1;
      
      const { data, error } = await supabase
        .from('inspection_template_items')
        .insert([{
          template_id: templateId!,
          system_id: systemId,
          item_key: itemData.item_key,
          name: itemData.name,
          description: itemData.description ?? null,
          is_required: itemData.is_required ?? false,
          evidence_mode: itemData.evidence_mode ?? 'OPTIONAL',
          allowed_evidence: itemData.allowed_evidence ?? { photo: true, note: true },
          ai_policy: itemData.ai_policy ?? null,
          measurement_schema: itemData.measurement_schema ?? null,
          default_job_catalog_ids: itemData.default_job_catalog_ids ?? null,
          sort_order: nextOrder,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-items', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to add item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ itemId, updates }: { itemId: string; updates: ItemUpdate }) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      const { error } = await supabase
        .from('inspection_template_items')
        .update(updates)
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-items', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      const { error } = await supabase
        .from('inspection_template_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-items', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete item',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const reorderItemsMutation = useMutation({
    mutationFn: async ({ systemId, orderedIds }: { systemId: string; orderedIds: string[] }) => {
      if (templateId) await verifyDraftStatus(templateId);
      
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('inspection_template_items')
          .update({ sort_order: index })
          .eq('id', id)
      );
      
      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-items', templateId] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to reorder items',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // ============================================
  // Return
  // ============================================

  const isDraft = templateQuery.data?.status === 'draft';
  const isGlobal = templateQuery.data?.is_global ?? false;
  const canEdit = isDraft && !isGlobal;

  return {
    // Queries
    templates: templatesQuery.data ?? [],
    currentTemplate: templateQuery.data ?? null,
    systems: systemsQuery.data ?? [],
    items: itemsQuery.data ?? [],
    versions: versionsQuery.data ?? [],
    isLoading: templatesQuery.isLoading || templateQuery.isLoading,
    isLoadingStructure: systemsQuery.isLoading || itemsQuery.isLoading,

    // State
    isDraft,
    isGlobal,
    canEdit,

    // RPC mutations
    createDraft: (templateKey: string, name: string, description?: string) =>
      createDraftMutation.mutateAsync({ templateKey, name, description }),
    publishDraft: (tid: string, changeNotes?: string) =>
      publishDraftMutation.mutateAsync({ templateId: tid, changeNotes }),
    archiveTemplate: (tid: string) =>
      archiveTemplateMutation.mutateAsync(tid),
    isCreatingDraft: createDraftMutation.isPending,
    isPublishing: publishDraftMutation.isPending,
    isArchiving: archiveTemplateMutation.isPending,

    // Direct mutations
    addSystem: (tid: string, data: NewSystem) =>
      addSystemMutation.mutateAsync({ templateId: tid, data }),
    updateSystem: (systemId: string, updates: SystemUpdate) =>
      updateSystemMutation.mutateAsync({ systemId, updates }),
    deleteSystem: (systemId: string) =>
      deleteSystemMutation.mutateAsync(systemId),
    reorderSystems: (tid: string, orderedIds: string[]) =>
      reorderSystemsMutation.mutateAsync({ templateId: tid, orderedIds }),

    addItem: (systemId: string, data: NewItem) =>
      addItemMutation.mutateAsync({ systemId, data }),
    updateItem: (itemId: string, updates: ItemUpdate) =>
      updateItemMutation.mutateAsync({ itemId, updates }),
    deleteItem: (itemId: string) =>
      deleteItemMutation.mutateAsync(itemId),
    reorderItems: (systemId: string, orderedIds: string[]) =>
      reorderItemsMutation.mutateAsync({ systemId, orderedIds }),

    isSaving:
      addSystemMutation.isPending ||
      updateSystemMutation.isPending ||
      deleteSystemMutation.isPending ||
      reorderSystemsMutation.isPending ||
      addItemMutation.isPending ||
      updateItemMutation.isPending ||
      deleteItemMutation.isPending ||
      reorderItemsMutation.isPending,
  };
}
